import { extractLetters } from "@/helpers/common";
import {
  COLORS,
  PANEL_TYPE_OTHER,
  PANEL_TYPE_OTHER_CUSTOM,
  PANEL_TYPE_SERVICE,
  SERVICES_COLOR_CODES,
  SERVICE_APPLICATION_DATA,
  SERVICE_CUSTOM_ATTRIBUTES,
  TIME_IN_BUSINESS_SERVICE_EXCEPTIONS,
  TYPE_BOOLEAN,
  TYPE_EQUAL,
  TYPE_GREATER_THAN,
  TYPE_GREATER_THAN_OR_EQUAL,
  TYPE_IN_CASE_INSENSITIVE,
  TYPE_IN_FLEXIBLE,
  TYPE_LESS_THAN,
  TYPE_LESS_THAN_OR_EQUAL,
  TYPE_NAICS_IN_RANGE,
  TYPE_NAICS_NOT_IN_RANGE,
  TYPE_NOT_EQUAL,
  TYPE_NOT_IN_CASE_INSENSITIVE,
  TYPE_NOT_IN_FLEXIBLE,
  TYPE_RANGE,
} from "@/helpers/constants";
import { CONDITION_NUMBER_COMPARISON_TYPES } from "@/helpers/constants/orchestration";
import {
  makeRandomString,
  wordsFirstLetterToUpper,
} from "@/helpers/formatting";
import i18n from "@/i18n";
import store from "@/store";

import type { ConditionType, Nullish } from "@/models/common";
import type { ErrorsById } from "@/models/errors";
import type {
  IConditionalSetValueModel,
  IDataService,
  IDataServices,
  IRules,
  IStep,
  NaicsRange,
  PrimaryAttributes,
  SyncOrFillEligibleAttributes,
} from "@/models/orchestration";
import type { Edge as VueFlowEdge } from "@vue-flow/core";

export const getDoStepBlueprint = (data: IDataService): IStep => {
  return {
    id: makeRandomString(15),
    attributes: [],
    title: data.title,
    provider: data.provider,
    outcomes: [],
  };
};

/**
 * Collects the ids of all steps reachable from `rootStepId` via outcome.next_step,
 * including `rootStepId` itself. Each node is visited once, so shared downstream
 * branches are returned exactly once.
 */
export const getDescendantStepIds = (
  steps: IStep[],
  rootStepId: string
): Set<string> => {
  const visited = new Set<string>();

  const walk = (id: string) => {
    if (visited.has(id)) {
      return;
    }
    visited.add(id);

    const step = steps.find((s) => s.id === id);
    if (!step) {
      return;
    }

    step.outcomes.forEach((outcome) => {
      if (outcome.next_step) {
        walk(outcome.next_step);
      }
    });
  };

  walk(rootStepId);
  return visited;
};

export const getServiceColor = (service: string | null | undefined): string => {
  if (!service) {
    return "rgb(0, 0, 0)";
  }
  return `rgb(${
    COLORS[SERVICES_COLOR_CODES[service as keyof typeof SERVICES_COLOR_CODES]]
  })`;
};

export const getIsConditionValueInvalid = ({
  value,
  secondValue,
  type,
}: IConditionalSetValueModel) => {
  if (!CONDITION_NUMBER_COMPARISON_TYPES.includes(type)) {
    return false;
  }

  return (
    !String(value).length ||
    value === "null" ||
    value === "undefined" ||
    !String(secondValue).length ||
    secondValue === "null" ||
    secondValue === "undefined"
  );
};

export const validateDataOrchestrationPayload = (
  steps: IStep[]
): ErrorsById => {
  const t = i18n.global.t;
  const errorsByStepId: ErrorsById = {};
  const services: IDataServices = store.getters["orchestration/activeServices"];
  const providerIdToTitleMap = new Map<string, string>();

  services.all_data_services?.forEach(({ services }) => {
    services?.forEach(({ id, name }) => {
      providerIdToTitleMap.set(String(id), name);
    });
  });

  if (!steps.length) {
    errorsByStepId["template"] = {
      title: "Template",
      errors: [t("ORCHESTRATION.VALIDATION.STEP_REQUIRED")],
    };
  }
  steps.forEach((step) => {
    const stepId = step.id;
    const providerTitle = step.provider
      ? providerIdToTitleMap.get(String(step.provider))
      : null;

    let stepTitle =
      step.title || t("ORCHESTRATION.VALIDATION.TITLE_PLACEHOLDER");

    if (providerTitle) {
      stepTitle = `${providerTitle} ${stepTitle}`;
    }

    if (!errorsByStepId[stepId]) {
      errorsByStepId[stepId] = { title: stepTitle, errors: [] };
    }

    if (!step.outcomes.length) {
      errorsByStepId[stepId].errors.push(
        t("ORCHESTRATION.VALIDATION.STEP_OUTCOME_REQUIRED")
      );
    }

    if (step.attributes?.length) {
      step.attributes.forEach((attribute) => {
        if (!attribute.options) {
          return;
        }

        const invalid = Object.values(attribute.options).filter(
          (val) => typeof val === "number" && (val < 0 || val % 1 !== 0)
        );
        if (invalid.length) {
          errorsByStepId[stepId].errors.push(
            t("ORCHESTRATION.VALIDATION.OPTIONS_TYPE")
          );
        }
      });
    } else {
      errorsByStepId[stepId].errors.push(
        t("ORCHESTRATION.VALIDATION.STEP_ATTRIBUTE_REQUIRED")
      );
    }

    const seenStepOutcomeChecks = new Map<string, string>();

    step.outcomes.forEach((outcome) => {
      const outcomeTitle = outcome.outcome;
      const outcomeId = `${stepId}-${extractLetters(outcomeTitle)}`;

      if (!errorsByStepId[outcomeId]) {
        errorsByStepId[outcomeId] = {
          title: `${stepTitle} - ${outcomeTitle.toUpperCase()}`,
          errors: [],
        };
      }

      if (!outcome.checks?.length) {
        errorsByStepId[outcomeId].errors.push(
          t("ORCHESTRATION.VALIDATION.OUTCOME_CONDITION_REQUIRED")
        );
        return;
      }

      const outcomeCheckSignature = JSON.stringify(outcome.checks || []);
      if (seenStepOutcomeChecks.has(outcomeCheckSignature)) {
        errorsByStepId[outcomeId].errors.push(
          t("ORCHESTRATION.VALIDATION.OUTCOME_CONDITION_ARGUMENT_DUPLICATE")
        );
        const firstOutcomeId = seenStepOutcomeChecks.get(
          outcomeCheckSignature
        )!;

        errorsByStepId[firstOutcomeId].errors.push(
          t("ORCHESTRATION.VALIDATION.OUTCOME_CONDITION_ARGUMENT_DUPLICATE")
        );
      } else {
        seenStepOutcomeChecks.set(outcomeCheckSignature, outcomeId);
      }

      outcome.checks.forEach((check) => {
        if (!check.conditions?.length) {
          errorsByStepId[outcomeId].errors.push(
            t("ORCHESTRATION.VALIDATION.OUTCOME_CONDITION_REQUIRED")
          );
          return;
        }

        check.conditions.forEach((checkCondition) => {
          if (!checkCondition.conditions?.length) {
            errorsByStepId[outcomeId].errors.push(
              t("ORCHESTRATION.VALIDATION.OUTCOME_CONDITION_VALUE_REQUIRED")
            );
          }

          const invalidErr = t(
            "ORCHESTRATION.VALIDATION.OUTCOME_CONDITION_VALUE_INVALID"
          );

          if (!errorsByStepId[outcomeId].errors.includes(invalidErr)) {
            const hasInvalid = checkCondition.conditions.some(
              getIsConditionValueInvalid
            );
            if (hasInvalid) {
              errorsByStepId[outcomeId].errors.push(invalidErr);
            }
          }
        });
      });
    });
  });

  return errorsByStepId;
};
export const getRuleTypes = (rules: IRules | undefined, argId?: string) => {
  if (!rules) {
    return {};
  }
  const t = i18n.global.t;
  const OPTIONS_WITH_SYMBOLS_OR_MODIFICATIONS: Record<string, string> = {
    [TYPE_LESS_THAN]: t(`FIELD_DICTIONARY.${TYPE_LESS_THAN}`),
    [TYPE_LESS_THAN_OR_EQUAL]: t(`FIELD_DICTIONARY.${TYPE_LESS_THAN_OR_EQUAL}`),
    [TYPE_GREATER_THAN]: t(`FIELD_DICTIONARY.${TYPE_GREATER_THAN}`),
    [TYPE_GREATER_THAN_OR_EQUAL]: t(
      `FIELD_DICTIONARY.${TYPE_GREATER_THAN_OR_EQUAL}`
    ),
    [TYPE_EQUAL]: t(`FIELD_DICTIONARY.${TYPE_EQUAL}`),
    [TYPE_NOT_EQUAL]: t(`FIELD_DICTIONARY.${TYPE_NOT_EQUAL}`),
    [TYPE_BOOLEAN]: t(`FIELD_DICTIONARY.${TYPE_BOOLEAN}`),
    [TYPE_IN_FLEXIBLE]: t("FIELD_DICTIONARY.in"),
    [TYPE_NOT_IN_FLEXIBLE]: t("FIELD_DICTIONARY.not_in"),
    [TYPE_IN_CASE_INSENSITIVE]: t("FIELD_DICTIONARY.in"),
    [TYPE_NOT_IN_CASE_INSENSITIVE]: t("FIELD_DICTIONARY.not_in"),
  };

  const [, actualRules] = rules.type.split(":");
  const typesArray = actualRules.split(",");
  const shouldAppendRangeType =
    !getIsTimeInBusiness(argId || "") &&
    [TYPE_LESS_THAN_OR_EQUAL, TYPE_GREATER_THAN_OR_EQUAL].every((rule) =>
      typesArray.includes(rule)
    );

  const types = typesArray.reduce<Record<string, string>>(
    (acc, curr) => ({
      ...acc,
      [curr]:
        OPTIONS_WITH_SYMBOLS_OR_MODIFICATIONS[curr] ||
        wordsFirstLetterToUpper(curr, "_"),
    }),
    {}
  );

  if (!shouldAppendRangeType) {
    return types;
  }

  return {
    ...types,
    [TYPE_RANGE]: t(`FIELD_DICTIONARY.${TYPE_RANGE}`),
  };
};

export const getHasOneOrLessOptions = (types: Record<string, string>) => {
  const options = types || {};
  return Object.keys(options).length <= 1;
};

/**
 * Map the given DO attribute id to a sync context attribute.
 *
 * For example, 'sentilink_completion_confidence_score_dob' supports syncing
 * date of birth on the primary owner by specifying 'dob' in the list of attributes
 * on the sync context.
 *
 * @param {string} attribute The DO attribute id
 * @returns {string|null}
 */
export const syncContextAttribute = (attribute: string) =>
  SYNC_MAP[attribute.toLowerCase()] || null;

const SYNC_MAP: Record<string, string> = {
  sentilink_dob_completion_dob: "dob",
  sentilink_ssn_completion_confidence_score_ssn: "ssn",
  middesk_business_search_name_search: "business_name",
};

const SYNC_OR_FILL_MAP: Record<string, SyncOrFillEligibleAttributes[]> = {
  address_line: [
    "persona_dl_address",
    "persona_pp_address",
    "persona_id_address",
    "clear_id_confirm_business_street_match_score",
    "clear_id_confirm_person_street_match_score",
    "lexis_nexis_kyc_has_street_number",
    "lexis_nexis_kyc_has_street",
  ],
  address_line2: [
    "persona_dl_address_line2",
    "persona_pp_address_line2",
    "persona_id_address_line2",
  ],
  city: [
    "persona_dl_city",
    "persona_pp_city",
    "persona_id_city",
    "clear_id_confirm_business_city_match_score",
    "clear_id_confirm_person_city_match_score",
    "lexis_nexis_kyc_has_city",
  ],
  state: [
    "persona_dl_state",
    "persona_pp_state",
    "persona_id_state",
    "clear_id_confirm_business_state_match_score",
    "clear_id_confirm_person_state_match_score",
    "lexis_nexis_kyc_has_state",
  ],
  zip: [
    "persona_dl_zip",
    "persona_pp_zip",
    "persona_id_zip",
    "clear_id_confirm_business_zip_code_match_score",
    "clear_id_confirm_person_zip_code_match_score",
    "lexis_nexis_kyc_has_zip_code",
  ],
  telephone: [
    "clear_id_confirm_business_phone_number_match_score",
    "clear_id_confirm_person_phone_number_match_score",
    "lexis_nexis_kyc_report_has_telephone",
    "lexis_nexis_kyb_search_has_phone_number",
    "lexis_nexis_contact_card_report_has_phone_number",
  ],
  ein: ["clear_id_confirm_business_fein_match_score"],
  dob: [
    "persona_dl_date_of_birth",
    "persona_pp_date_of_birth",
    "persona_id_date_of_birth",
    "lexis_nexis_kyc_has_date_of_birth",
  ],
  first_name: [
    "persona_dl_first_name",
    "persona_pp_first_name",
    "persona_id_first_name",
    "clear_id_confirm_business_officer_agent_first_name_match_score",
    "lexis_nexis_kyc_has_first_name",
  ],
  last_name: [
    "persona_dl_last_name",
    "persona_pp_last_name",
    "persona_id_last_name",
    "clear_id_confirm_business_officer_agent_last_name_match_score",
    "lexis_nexis_kyc_has_last_name",
  ],
  full_address: [
    "persona_dl_full_address",
    "persona_id_full_address",
    "persona_pp_full_address",
    "lexis_nexis_email_search_has_address",
    "lexis_nexis_kyc_report_full_address",
    "lexis_nexis_kyc_report_has_residential_address",
    "lexis_nexis_kyc_report_has_business_address",
    "lexis_nexis_kyb_search_has_address",
    "lexis_nexis_contact_card_report_has_address",
    "lexis_nexis_contact_card_report_has_owner_address",
  ],
  naics_code: [
    "dnb_ci_l2_naics_code",
    "middesk_naics_code",
    "experian_business_facts_naics_code",
    "clear_risk_inform_business_report_naics_code",
    "lexis_nexis_kyb_report_naics_code",
  ],
  sic_code: [
    "dnb_ci_l2_sic_code",
    "experian_business_facts_sic_code",
    "clear_risk_inform_business_report_sic_code",
    "lexis_nexis_kyb_report_sic_code",
  ],
  additional_business_stated_average_balance: [
    "ocrolus_cfa_average_daily_balance",
    "ocrolus_cfa_v2_average_daily_balance",
    "moneythumb_cfa_average_balance",
  ],
  funding_actual_average_monthly_revenue: [
    "ocrolus_cfa_average_estimated_revenue",
    "ocrolus_cfa_v2_revenue_by_month",
    "moneythumb_cfa_total_credits",
  ],
  email: [
    "lexis_nexis_email_search_has_email",
    "lexis_nexis_kyc_report_has_email",
    "lexis_nexis_kyb_search_has_email",
  ],
  additional_business_operating_expense: [
    "moneythumb_cfa_monthly_expense",
    "ocrolus_cfa_v2_expense_by_month",
    "ocrolus_cfa_withdrawal_sum",
  ],
  full_name: [
    "lexis_nexis_kyb_search_has_owner_name",
    "lexis_nexis_contact_card_report_has_owner_name",
  ],
  dot_number: ["fmcsa_usdot_number"],
  usdot_status: ["fmcsa_usdot_status"],
  entity_type: ["fmcsa_entity_type"],
  power_units: ["fmcsa_power_units"],
  operation_classification: ["fmcsa_operation_classification"],
  cargo_carried: ["fmcsa_cargo_carried"],
  authority_status_broker: ["fmcsa_authority_status_broker"],
  authority_status_common: ["fmcsa_authority_status_common"],
  insurance_on_file: ["fmcsa_insurance_on_file"],
  insurance_required: ["fmcsa_insurance_required"],
  additional_telephones: [
    "clear_risk_inform_business_search_add_phone_numbers_to_business",
    "clear_risk_inform_business_report_add_phone_numbers_to_business",
    "clear_id_confirm_business_add_phone_numbers_to_business",
  ],
  other_owners: [
    "lexis_nexis_kyc_add_owners_to_business",
    "lexis_nexis_kyc_report_add_owner_to_business",
  ],
};

const PRIMARY_ATTRIBUTES_MAP: Record<string, PrimaryAttributes[]> = {
  full_address: [
    "persona_dl_full_address",
    "persona_pp_full_address",
    "persona_id_full_address",
    "lexis_nexis_email_search_has_address",
    "lexis_nexis_kyc_report_full_address",
    "lexis_nexis_kyc_report_has_residential_address",
    "lexis_nexis_kyc_report_has_business_address",
    "lexis_nexis_kyb_search_has_address",
    "lexis_nexis_contact_card_report_has_address",
    "lexis_nexis_contact_card_report_has_owner_address",
  ],
  email: [
    "lexis_nexis_email_search_has_email",
    "lexis_nexis_kyc_report_has_email",
    "lexis_nexis_kyb_search_has_email",
  ],
  telephone: [
    "lexis_nexis_kyc_report_has_telephone",
    "lexis_nexis_kyb_search_has_phone_number",
    "lexis_nexis_contact_card_report_has_phone_number",
  ],
  additional_telephones: [
    "clear_risk_inform_business_search_add_phone_numbers_to_business",
    "clear_risk_inform_business_report_add_phone_numbers_to_business",
    "clear_id_confirm_business_add_phone_numbers_to_business",
  ],
  other_owners: [
    "lexis_nexis_kyc_add_owners_to_business",
    "lexis_nexis_kyc_report_add_owner_to_business",
  ],
};

export const syncOrFillContextAttribute = (
  attribute: SyncOrFillEligibleAttributes
) => {
  return (
    Object.keys(SYNC_OR_FILL_MAP).find((key) =>
      SYNC_OR_FILL_MAP[key].includes(attribute)
    ) || null
  );
};

export const getPrimaryContextAttribute = (attribute: PrimaryAttributes) => {
  return (
    (
      Object.keys(
        PRIMARY_ATTRIBUTES_MAP
      ) as (keyof typeof PRIMARY_ATTRIBUTES_MAP)[]
    ).find((key) => PRIMARY_ATTRIBUTES_MAP[key].includes(attribute)) || null
  );
};

const YEAR_ATTRIBUTES: string[] = [
  "additional_child_tax_credit",
  "adjusted_gross_income",
  "american_opportunity_credit",
  "amount_owed",
  "business_tax_return_full_response",
  "capital_gain_or_loss",
  "depreciation",
  "earned_income_credit",
  "estimated_tax_payments",
  "estimated_tax_penalty",
  "federal_tax_withheld_1099",
  "federal_tax_withheld_other_forms",
  "federal_tax_withheld_w2",
  "ordinary_dividends",
  "other_income_or_loss",
  "personal_tax_return_full_response",
  "qualified_dividends",
  "refund_amount",
  "refund_amount_applied_to_next_year",
  "self_employement_tax",
  "standard_deduction",
  "taxable_interest",
  "taxable_ira_distributions",
  "taxable_pensions_annuities",
  "taxable_social_security",
  "total_income",
  "total_payments",
  "total_tax",
  "wages_total",
  "form_1120s_accounting_method",
  "form_1120s_advertising",
  "form_1120s_cost_of_goods_sold",
  "form_1120s_depreciation",
  "form_1120s_gross_profit",
  "form_1120s_gross_receipts_or_sales",
  "form_1120s_net_gain_or_loss_form_4797",
  "form_1120s_ordinary_business_income_or_loss",
  "form_1120s_other_deductions",
  "form_1120s_rents",
  "form_1120s_repairs_and_maintenance",
  "form_1120s_total_assets",
  "form_1120s_total_deductions",
  "form_1120s_total_income_or_loss",
  "form_1120s_total_shares_outstanding",
  "schedule_k1_adjusted_gain_or_loss",
  "schedule_k1_distributions",
  "schedule_k1_net_section_1231_gain_or_loss",
  "schedule_k1_ordinary_business_income_or_loss",
  "schedule_k1_post_1986_depreciation_adjustment",
  "schedule_l_accounts_payable_beginning_of_year",
  "schedule_l_accounts_payable_end_of_year",
  "schedule_l_cash_beginning_of_year",
  "schedule_l_cash_end_of_year",
  "schedule_l_total_assets_beginning_of_year",
  "schedule_l_total_assets_end_of_year",
  "schedule_l_total_liabilities_beginning_of_year",
  "schedule_l_total_liabilities_end_of_year",
  "schedule_l_total_liabilities_and_equity_beginning_of_year",
  "schedule_l_total_liabilities_and_equity_end_of_year",
  "schedule_m1_income_or_loss_schedule_k",
  "schedule_m1_net_income_or_loss_per_books",
];

export const isSpecificYearAttribute = (attribute: string) =>
  YEAR_ATTRIBUTES.indexOf(attribute) > -1;

/**
 * Is the given DO attribute id a DO attribute that supports syncing?
 *
 * @param {string} attribute
 * @returns {boolean}
 */
export const supportsSyncContext = (attribute: string) =>
  syncContextAttribute(attribute) !== null;

export const supportsSyncOrFillContext = (
  attribute: SyncOrFillEligibleAttributes
) => syncOrFillContextAttribute(attribute) !== null;

export const supportsPrimaryContext = (attribute: PrimaryAttributes) =>
  getPrimaryContextAttribute(attribute) !== null;

export const getIsTimeInBusiness = (id: string) => {
  return (
    TIME_IN_BUSINESS_SERVICE_EXCEPTIONS.every(
      (exceptionId) => !id.startsWith(exceptionId)
    ) && id.endsWith("_time_in_business")
  );
};

export const requiresYearSelection = (attribute: string) =>
  isSpecificYearAttribute(attribute);

export const filterInvalidValueOptions = (
  options: Record<string, string>
): Record<string, string> =>
  Object.fromEntries(
    Object.entries(options).filter(([key]) => {
      return !["consumer_score_models_levels"].includes(key);
    })
  );

export const typeIsNaicsCodeRange = (type: ConditionType) =>
  type === TYPE_NAICS_IN_RANGE || type === TYPE_NAICS_NOT_IN_RANGE;

export const haveNaicsRangeValues = (condition: IConditionalSetValueModel) => {
  if (
    !condition?.value ||
    typeof condition !== "object" ||
    Array.isArray(condition)
  ) {
    return false;
  }
  const value = condition.value as NaicsRange;
  return "from" in value && "through" in value && "digits" in value;
};

export const isNullishAttributeWithContext = (id: string) => {
  const endings = [
    "add_phone_numbers_to_business",
    "add_owners_to_business",
    "add_owner_to_business",
  ];
  return endings.some((ending) => id.endsWith(ending));
};

export const getStepSidebarType = (
  step: IStep | Nullish
):
  | typeof PANEL_TYPE_OTHER
  | typeof PANEL_TYPE_OTHER_CUSTOM
  | typeof PANEL_TYPE_SERVICE => {
  switch (step?.title) {
    case SERVICE_APPLICATION_DATA:
      return PANEL_TYPE_OTHER;
    case SERVICE_CUSTOM_ATTRIBUTES:
      return PANEL_TYPE_OTHER_CUSTOM;
    default:
      return PANEL_TYPE_SERVICE;
  }
};

export const getAllDescendantIds = (
  nodeId: string,
  edges: VueFlowEdge[]
): Set<string> => {
  const descendants = new Set<string>();
  const stack = [nodeId];

  while (stack.length) {
    const currentNodeId = stack.pop()!;
    for (const edge of edges) {
      if (edge.source === currentNodeId && !descendants.has(edge.target)) {
        descendants.add(edge.target);
        stack.push(edge.target);
      }
    }
  }

  return descendants;
};

/**
 * Check if connecting source to target would create a cycle.
 * A cycle is created if sourceNodeId is a descendant of targetNodeId
 * (meaning target can already reach source, so source->target would close the loop).
 */
export const wouldCreateCycle = (
  sourceNodeId: string,
  targetNodeId: string,
  edges: VueFlowEdge[]
): boolean => {
  if (sourceNodeId === targetNodeId) {
    return true;
  }

  const targetDescendants = getAllDescendantIds(targetNodeId, edges);
  return targetDescendants.has(sourceNodeId);
};
